import React, { useEffect, useRef, useState, useCallback, useMemo } from 'react';
import { apiService } from '../services/apiService';
import { useTranslation } from 'react-i18next';
import { TableSkeleton } from './SkeletonLoader';
import { useToast, ConfirmDialog } from './Toast';

// 可调整列宽的表头组件
const ResizableSortableHeader = ({
    children,
    columnKey,
    sortBy,
    sortDirection,
    setSortBy,
    setSortDirection,
    width,
    onResize,
    minWidth = 80,
    frozenClass = '',
    frozenStyle = {}
}) => {
    const isActive = sortBy === columnKey;
    const newDirection = isActive && sortDirection === 'asc' ? 'desc' : 'asc';
    const headerRef = useRef(null);
    const isResizing = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    const handleClick = (e) => {
        // 防止拖拽时触发排序
        if (isResizing.current) return;
        setSortBy(columnKey);
        setSortDirection(newDirection);
    };

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        isResizing.current = true;
        startX.current = e.clientX;
        startWidth.current = width || headerRef.current?.offsetWidth || 120;

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (e) => {
            if (!isResizing.current) return;
            const diff = e.clientX - startX.current;
            const newWidth = Math.max(minWidth, startWidth.current + diff);
            onResize(columnKey, newWidth);
        };

        const handleMouseUp = () => {
            isResizing.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [columnKey, minWidth, onResize, width]);

    return (
        <th
            ref={headerRef}
            className={`relative px-6 py-4 text-left text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider cursor-pointer hover:bg-[var(--neutral-100)] transition-colors duration-200 select-none group ${frozenClass}`}
            style={{ width: width ? `${width}px` : 'auto', minWidth: `${minWidth}px`, ...frozenStyle }}
            onClick={handleClick}
        >
            <div className="flex items-center gap-2 pr-2">
                <span className="min-w-0 flex-1 truncate">{children}</span>
                <span className={`flex-shrink-0 transition-transform duration-200 ${isActive ? 'text-[var(--primary-500)]' : 'text-[var(--neutral-300)]'}`}>
                    {isActive ? (sortDirection === 'asc' ? '▲' : '▼') : '↕'}
                </span>
            </div>
            {/* 拖拽手柄 */}
            <div
                className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-[var(--primary-400)] group-hover:bg-[var(--neutral-300)] transition-colors duration-150"
                onMouseDown={handleMouseDown}
                onClick={(e) => e.stopPropagation()}
                title="ドラッグで列幅を変更"
            />
        </th>
    );
};

// 固定列表头（不可排序但可调整宽度）
const ResizableHeader = ({
    children,
    columnKey,
    width,
    onResize,
    minWidth = 60,
    className = "",
    frozenClass = '',
    frozenStyle = {}
}) => {
    const headerRef = useRef(null);
    const isResizing = useRef(false);
    const startX = useRef(0);
    const startWidth = useRef(0);

    const handleMouseDown = useCallback((e) => {
        e.preventDefault();
        e.stopPropagation();
        isResizing.current = true;
        startX.current = e.clientX;
        startWidth.current = width || headerRef.current?.offsetWidth || 80;

        document.body.style.cursor = 'col-resize';
        document.body.style.userSelect = 'none';

        const handleMouseMove = (e) => {
            if (!isResizing.current) return;
            const diff = e.clientX - startX.current;
            const newWidth = Math.max(minWidth, startWidth.current + diff);
            onResize(columnKey, newWidth);
        };

        const handleMouseUp = () => {
            isResizing.current = false;
            document.body.style.cursor = '';
            document.body.style.userSelect = '';
            document.removeEventListener('mousemove', handleMouseMove);
            document.removeEventListener('mouseup', handleMouseUp);
        };

        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
    }, [columnKey, minWidth, onResize, width]);

    return (
        <th
            ref={headerRef}
            className={`relative px-4 py-3 text-xs font-semibold text-[var(--text-secondary)] uppercase tracking-wider group ${className} ${frozenClass}`}
            style={{ width: width ? `${width}px` : 'auto', minWidth: `${minWidth}px`, ...frozenStyle }}
        >
            <span className="truncate">{children}</span>
            {/* 拖拽手柄 */}
            <div
                className="absolute right-0 top-0 h-full w-1 cursor-col-resize bg-transparent hover:bg-[var(--primary-400)] group-hover:bg-[var(--neutral-300)] transition-colors duration-150"
                onMouseDown={handleMouseDown}
                onClick={(e) => e.stopPropagation()}
                title="ドラッグで列幅を変更"
            />
        </th>
    );
};

export default function TalentTable({ talents, isLoading, feeTotals, userInfo, onDataRefresh, onEdit, onViewDetails, onViewEdit, onTransferTalent, sortBy, setSortBy, sortDirection, setSortDirection }) {
    const [isDeleting, setIsDeleting] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, talent: null });
    const [scrollWidth, setScrollWidth] = useState(0);
    const [isColumnMenuOpen, setIsColumnMenuOpen] = useState(false);
    const topScrollRef = useRef(null);
    const mainScrollRef = useRef(null);
    const columnMenuRef = useRef(null);
    const isSyncingScrollRef = useRef(false);
    const { t } = useTranslation();
    const { showSuccess, showError } = useToast();

    // 列宽状态管理
    const [columnWidths, setColumnWidths] = useState({
        serialNumber: 60,
        fullName: 120,
        managementNumber: 120,
        affiliatedOrganizationName: 180,
        workplaceName: 180,
        initialCost: 160,
        managementFee: 160,
        nationality: 100,
        field: 120,
        salesStaff: 120,
        residenceCardNumber: 160,
        residenceCardExpiry: 150,
        residenceInfoUpdatedAt: 170,
        startDate: 130,
        endDate: 130,
        employmentDays: 120,
        activeLoanBorrowedAmount: 130,
        activeLoanRepaidAmount: 130,
        activeLoanRemainingAmount: 130,
        status: 120,
        actions: 200
    });

    // 处理列宽调整
    const handleColumnResize = useCallback((columnKey, newWidth) => {
        setColumnWidths(prev => ({
            ...prev,
            [columnKey]: newWidth
        }));
    }, []);

    // 国家名称和分野的日语翻译函数
    const getJapaneseNationality = (nationality) => {
        const nationalityMap = {
            "中国": "中国",
            "韩国": "韓国",
            "缅甸": "ミャンマー",
            "印度尼西亚": "インドネシア",
            "越南": "ベトナム",
            "台湾": "台湾",
            "日本": "日本",
            "菲律宾": "フィリピン",
            "泰国": "タイ"
        };
        return nationalityMap[nationality] || nationality;
    };

    const getJapaneseField = (field) => {
        const fieldMap = {
            "外食": "外食",
            "飲食料品製造": "飲食料品製造",
            "溶接": "溶接",
            "介护": "介護",
            "介護": "介護",
            "他": "他"
        };
        return fieldMap[field] || field;
    };

    const formatYen = (amount) => {
        if (amount === null || amount === undefined || amount === '') return '';
        return `${Number(amount).toLocaleString('ja-JP')}円`;
    };

    const renderFeeHeader = (label, amount) => {
        const hasTotal = feeTotals !== null && feeTotals !== undefined;
        const totalText = hasTotal ? formatYen(amount) : '—';

        return (
            <span
                className="block min-w-0 leading-tight normal-case tracking-normal"
                title={`${label}${t('totalLabel')}: ${totalText}`}
            >
                <span className="block truncate">{label}</span>
                <span className="mt-1 block truncate text-[11px] font-bold tabular-nums text-[var(--primary-700)]">
                    {t('totalLabel')} {totalText}
                </span>
            </span>
        );
    };

    const formatDateValue = (value) => {
        if (!value) {
            return <span className="status-badge status-inactive">{t('unregistered')}</span>;
        }
        const str = String(value);
        return str.includes('T') ? str.split('T')[0] : str;
    };

    const parseDateOnlyValue = (value) => {
        if (!value) return null;
        const normalized = String(value).split('T')[0];
        const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
        if (!match) return null;

        const year = Number(match[1]);
        const month = Number(match[2]);
        const day = Number(match[3]);

        if (!Number.isInteger(year) || !Number.isInteger(month) || !Number.isInteger(day)) {
            return null;
        }

        return new Date(Date.UTC(year, month - 1, day));
    };

    const getTodayUtcDateOnly = () => {
        const now = new Date();
        return new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
    };

    const getEmploymentDays = (talent) => {
        const residence = talent?.residence || talent?.residencePermit;
        const startDate = parseDateOnlyValue(residence?.startDate);
        if (!startDate) {
            return null;
        }

        const explicitEndDate = parseDateOnlyValue(residence?.endDate);
        let endDate = explicitEndDate;

        if (!endDate) {
            if (talent?.employmentStatus === '離職') {
                return null;
            }

            endDate = getTodayUtcDateOnly();
        }

        if (endDate < startDate) {
            return null;
        }

        return Math.floor((endDate.getTime() - startDate.getTime()) / 86400000) + 1;
    };

    const formatEmploymentDays = (talent) => {
        const days = getEmploymentDays(talent);
        if (days === null) {
            return <span className="status-badge status-inactive">{t('unregistered')}</span>;
        }

        return `${days}日`;
    };

    const getLoanMetricValue = (talent, key) => {
        const pascalKey = key.charAt(0).toUpperCase() + key.slice(1);
        const value = talent?.[key] ?? talent?.[pascalKey];
        if (value === null || value === undefined || value === '') {
            return 0;
        }
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : 0;
    };

    const getCostDisplay = (amount, notCharged, treatZeroAsUnregistered = false) => {
        if (notCharged) {
            return <span className="status-badge status-pending">無償</span>;
        }
        if (treatZeroAsUnregistered && Number(amount) === 0) {
            return <span className="status-badge status-inactive">{t('unregistered')}</span>;
        }
        if (amount === null || amount === undefined || amount === '') {
            return <span className="status-badge status-inactive">{t('unregistered')}</span>;
        }
        return formatYen(amount);
    };

    const getEmploymentStatusClass = (status, pauseType) => {
        if (status === '在職' || status === t('employed')) {
            return pauseType && pauseType !== 'なし' ? 'status-inactive' : 'status-active';
        }
        if (status === '内定' || status === t('jobOffer')) return 'status-active';
        if (status === '申請中' || status === t('applying')) return 'status-pending';
        if (status === '離職' || status === t('unemployed')) return 'status-error';
        if (status === '待入国' || status === t('awaitingEntry')) return 'status-pending';
        return 'status-inactive';
    };

    const getEmploymentStatusLabel = (talent) => {
        if (talent?.employmentStatus === '在職' && talent?.employmentPauseType && talent.employmentPauseType !== 'なし') {
            return `${t('employed')}・${talent.employmentPauseType}`;
        }
        return talent?.employmentStatus || t('employed');
    };

    const handleDeleteClick = (talent) => {
        setDeleteConfirm({ isOpen: true, talent });
    };

    const handleDeleteConfirm = async () => {
        const talent = deleteConfirm.talent;
        if (!talent) return;

        setIsDeleting(talent.id);
        setDeleteConfirm({ isOpen: false, talent: null });

        try {
            await apiService.deleteTalent(talent.id);
            showSuccess(t('deleteSuccess', { name: talent.fullName }));
            onDataRefresh();
        } catch (error) {
            console.error('删除失败:', error);
            showError(`${t('deleteFailed')}：${error.message}`);
        } finally {
            setIsDeleting(null);
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirm({ isOpen: false, talent: null });
    };

    const updateScrollWidth = useCallback(() => {
        const tableElement = mainScrollRef.current?.querySelector('table');
        if (tableElement) {
            setScrollWidth(tableElement.scrollWidth);
        }
    }, []);

    // Keep a top scrollbar in sync with the main one so users can scroll horizontally without jumping to the bottom.
    useEffect(() => {
        updateScrollWidth();
        window.addEventListener('resize', updateScrollWidth);
        return () => window.removeEventListener('resize', updateScrollWidth);
    }, [updateScrollWidth]);

    const syncScrollPosition = (sourceEl, targets) => {
        if (!sourceEl || isSyncingScrollRef.current) return;
        isSyncingScrollRef.current = true;
        targets.forEach(target => {
            if (target && target !== sourceEl) {
                target.scrollLeft = sourceEl.scrollLeft;
            }
        });
        requestAnimationFrame(() => { isSyncingScrollRef.current = false; });
    };

    const handleMainScroll = () => syncScrollPosition(mainScrollRef.current, [topScrollRef.current]);
    const handleTopScroll = () => syncScrollPosition(topScrollRef.current, [mainScrollRef.current]);

    const isAdmin = userInfo.role === 'Admin';
    const isSales = userInfo.role === 'Sales';
    const isResidence = userInfo.role === 'Residence';
    const showSerialNumber = isAdmin || isSales;
    const showResponsibleSales = isAdmin || isResidence;
    const supportsColumnModes = isAdmin || isSales || isResidence;
    const canViewResidenceColumns = isAdmin || isSales || isResidence;
    const canViewLoanColumns = isAdmin;
    const columnStorageKey = `talent_table_visible_columns_${userInfo.role || 'Unknown'}`;
    const defaultColumnMode = isResidence ? 'residenceInfo' : 'personalInfo';

    const columnModePresets = useMemo(() => {
        if (isAdmin) {
            return {
                personalInfo: [
                    'managementNumber',
                    'affiliatedOrganizationName',
                    'workplaceName',
                    'nationality',
                    'field',
                    'salesStaff',
                    'status',
                    'initialCost',
                    'managementFee'
                ],
                residenceInfo: [
                    'managementNumber',
                    'nationality',
                    'salesStaff',
                    'status',
                    'residenceCardNumber',
                    'residenceCardExpiry',
                    'residenceInfoUpdatedAt',
                    'startDate',
                    'endDate',
                    'employmentDays'
                ],
                loanInfo: [
                    'managementNumber',
                    'salesStaff',
                    'activeLoanBorrowedAmount',
                    'activeLoanRepaidAmount',
                    'activeLoanRemainingAmount'
                ]
            };
        }

        if (isSales) {
            return {
                personalInfo: [
                    'managementNumber',
                    'affiliatedOrganizationName',
                    'workplaceName',
                    'initialCost',
                    'managementFee',
                    'field',
                    'status'
                ],
                residenceInfo: [
                    'managementNumber',
                    'nationality',
                    'status',
                    'residenceCardNumber',
                    'residenceCardExpiry',
                    'residenceInfoUpdatedAt',
                    'startDate',
                    'endDate',
                    'employmentDays'
                ]
            };
        }

        if (isResidence) {
            return {
                residenceInfo: [
                    'managementNumber',
                    'nationality',
                    'salesStaff',
                    'status',
                    'residenceCardNumber',
                    'residenceCardExpiry',
                    'residenceInfoUpdatedAt',
                    'startDate',
                    'endDate',
                    'employmentDays'
                ]
            };
        }

        return {};
    }, [isAdmin, isResidence, isSales]);

    const columnModeOptions = useMemo(() => {
        if (isAdmin) {
            return [
                { key: 'personalInfo', label: '個人情報' },
                { key: 'residenceInfo', label: '在留情報' },
                { key: 'loanInfo', label: '貸付情報' }
            ];
        }

        if (isSales) {
            return [
                { key: 'personalInfo', label: '個人情報' },
                { key: 'residenceInfo', label: '在留情報' }
            ];
        }

        if (isResidence) {
            return [
                { key: 'residenceInfo', label: '在留情報' }
            ];
        }

        return [];
    }, [isAdmin, isResidence, isSales]);

    const columnModeLabels = useMemo(() => ({
        personalInfo: '個人情報',
        residenceInfo: '在留情報',
        loanInfo: '貸付情報',
        custom: 'カスタム'
    }), []);

    const optionalColumns = useMemo(() => {
        if (isResidence) {
            const defaultPreset = new Set(columnModePresets.residenceInfo || []);

            return [
                { key: 'managementNumber', label: t('managementNumber'), defaultVisible: defaultPreset.has('managementNumber'), modes: ['residenceInfo'] },
                { key: 'nationality', label: t('nationality'), defaultVisible: defaultPreset.has('nationality'), modes: ['residenceInfo'] },
                { key: 'salesStaff', label: t('responsibleSales'), defaultVisible: defaultPreset.has('salesStaff'), modes: ['residenceInfo'] },
                { key: 'status', label: t('employmentStatus'), defaultVisible: defaultPreset.has('status'), modes: ['residenceInfo'] },
                { key: 'residenceCardNumber', label: t('residenceCardNumber'), defaultVisible: defaultPreset.has('residenceCardNumber'), modes: ['residenceInfo'] },
                { key: 'residenceCardExpiry', label: t('residenceCardExpiry'), defaultVisible: defaultPreset.has('residenceCardExpiry'), modes: ['residenceInfo'] },
                { key: 'residenceInfoUpdatedAt', label: '在留情報更新日', defaultVisible: defaultPreset.has('residenceInfoUpdatedAt'), modes: ['residenceInfo'] },
                { key: 'startDate', label: t('startDate'), defaultVisible: defaultPreset.has('startDate'), modes: ['residenceInfo'] },
                { key: 'endDate', label: t('endDate'), defaultVisible: defaultPreset.has('endDate'), modes: ['residenceInfo'] },
                { key: 'employmentDays', label: t('employmentDays'), defaultVisible: defaultPreset.has('employmentDays'), modes: ['residenceInfo'] }
            ];
        }

        if (supportsColumnModes) {
            const defaultPreset = new Set(columnModePresets[defaultColumnMode] || []);
            return [
                { key: 'managementNumber', label: t('managementNumber'), defaultVisible: defaultPreset.has('managementNumber'), modes: ['personalInfo', 'residenceInfo', 'loanInfo'] },
                { key: 'affiliatedOrganizationName', label: t('affiliatedOrganization'), defaultVisible: defaultPreset.has('affiliatedOrganizationName'), modes: ['personalInfo'] },
                { key: 'workplaceName', label: t('workplace'), defaultVisible: defaultPreset.has('workplaceName'), modes: ['personalInfo'] },
                { key: 'initialCost', label: t('initialCost'), defaultVisible: defaultPreset.has('initialCost'), modes: ['personalInfo'] },
                { key: 'managementFee', label: t('managementFee'), defaultVisible: defaultPreset.has('managementFee'), modes: ['personalInfo'] },
                { key: 'nationality', label: t('nationality'), defaultVisible: defaultPreset.has('nationality'), modes: ['personalInfo', 'residenceInfo'] },
                { key: 'field', label: t('field'), defaultVisible: defaultPreset.has('field'), modes: ['personalInfo'] },
                showResponsibleSales ? { key: 'salesStaff', label: t('responsibleSales'), defaultVisible: defaultPreset.has('salesStaff'), modes: isAdmin ? ['personalInfo', 'residenceInfo', 'loanInfo'] : ['residenceInfo'] } : null,
                { key: 'status', label: t('employmentStatus'), defaultVisible: defaultPreset.has('status'), modes: ['personalInfo', 'residenceInfo'] },
                canViewResidenceColumns ? { key: 'residenceCardNumber', label: t('residenceCardNumber'), defaultVisible: defaultPreset.has('residenceCardNumber'), modes: ['residenceInfo'] } : null,
                canViewResidenceColumns ? { key: 'residenceCardExpiry', label: t('residenceCardExpiry'), defaultVisible: defaultPreset.has('residenceCardExpiry'), modes: ['residenceInfo'] } : null,
                canViewResidenceColumns ? { key: 'residenceInfoUpdatedAt', label: '在留情報更新日', defaultVisible: defaultPreset.has('residenceInfoUpdatedAt'), modes: ['residenceInfo'] } : null,
                canViewResidenceColumns ? { key: 'startDate', label: t('startDate'), defaultVisible: defaultPreset.has('startDate'), modes: ['residenceInfo'] } : null,
                canViewResidenceColumns ? { key: 'endDate', label: t('endDate'), defaultVisible: defaultPreset.has('endDate'), modes: ['residenceInfo'] } : null,
                canViewResidenceColumns ? { key: 'employmentDays', label: t('employmentDays'), defaultVisible: defaultPreset.has('employmentDays'), modes: ['residenceInfo'] } : null,
                canViewLoanColumns ? { key: 'activeLoanBorrowedAmount', label: '借入', defaultVisible: defaultPreset.has('activeLoanBorrowedAmount'), modes: ['loanInfo'] } : null,
                canViewLoanColumns ? { key: 'activeLoanRepaidAmount', label: '返済', defaultVisible: defaultPreset.has('activeLoanRepaidAmount'), modes: ['loanInfo'] } : null,
                canViewLoanColumns ? { key: 'activeLoanRemainingAmount', label: '残額', defaultVisible: defaultPreset.has('activeLoanRemainingAmount'), modes: ['loanInfo'] } : null
            ].filter(Boolean);
        }

        return [
            { key: 'managementNumber', label: t('managementNumber'), defaultVisible: true },
            { key: 'affiliatedOrganizationName', label: t('affiliatedOrganization'), defaultVisible: true },
            { key: 'workplaceName', label: t('workplace'), defaultVisible: false },
            { key: 'initialCost', label: t('initialCost'), defaultVisible: true },
            { key: 'managementFee', label: t('managementFee'), defaultVisible: true },
            { key: 'nationality', label: t('nationality'), defaultVisible: false },
            { key: 'field', label: t('field'), defaultVisible: true },
            { key: 'status', label: t('employmentStatus'), defaultVisible: true }
        ];
    }, [columnModePresets, canViewLoanColumns, canViewResidenceColumns, defaultColumnMode, isAdmin, isResidence, showResponsibleSales, supportsColumnModes, t]);

    const defaultVisibleOptionalColumns = useMemo(
        () => optionalColumns.filter(column => column.defaultVisible).map(column => column.key),
        [optionalColumns]
    );

    const readStoredColumns = useCallback(() => {
        const availableKeys = optionalColumns.map(column => column.key);
        const fallback = [...defaultVisibleOptionalColumns];

        try {
            const raw = localStorage.getItem(columnStorageKey);
            if (!raw) {
                return fallback;
            }

            const parsed = JSON.parse(raw);
            if (!Array.isArray(parsed)) {
                return fallback;
            }

            const sanitized = parsed.filter(key => availableKeys.includes(key));
            return sanitized.length > 0 ? sanitized : fallback;
        } catch (error) {
            return fallback;
        }
    }, [columnStorageKey, optionalColumns, defaultVisibleOptionalColumns]);

    const [visibleOptionalColumns, setVisibleOptionalColumns] = useState(() => readStoredColumns());
    const [selectedColumnMode, setSelectedColumnMode] = useState(defaultColumnMode);

    const menuColumns = useMemo(() => {
        if (!supportsColumnModes || selectedColumnMode === 'custom') {
            return optionalColumns;
        }

        return optionalColumns.filter(column => Array.isArray(column.modes) && column.modes.includes(selectedColumnMode));
    }, [optionalColumns, selectedColumnMode, supportsColumnModes]);

    useEffect(() => {
        setVisibleOptionalColumns(readStoredColumns());
    }, [readStoredColumns]);

    const inferColumnMode = useCallback((columns) => {
        if (!Array.isArray(columns)) return defaultColumnMode;

        const matchedMode = Object.entries(columnModePresets).find(([, preset]) => (
            columns.length === preset.length && preset.every(key => columns.includes(key))
        ));

        return matchedMode?.[0] || 'custom';
    }, [columnModePresets, defaultColumnMode]);

    useEffect(() => {
        if (!Array.isArray(visibleOptionalColumns)) return;
        localStorage.setItem(columnStorageKey, JSON.stringify(visibleOptionalColumns));
    }, [columnStorageKey, visibleOptionalColumns]);

    useEffect(() => {
        if (!supportsColumnModes) return;
        setSelectedColumnMode(inferColumnMode(visibleOptionalColumns));
    }, [inferColumnMode, supportsColumnModes, visibleOptionalColumns]);

    useEffect(() => {
        updateScrollWidth();
    }, [updateScrollWidth, visibleOptionalColumns, columnWidths, talents, userInfo.role]);

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (columnMenuRef.current && !columnMenuRef.current.contains(event.target)) {
                setIsColumnMenuOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const isColumnVisible = useCallback((key) => visibleOptionalColumns.includes(key), [visibleOptionalColumns]);

    const handleColumnToggle = (columnKey) => {
        setVisibleOptionalColumns(prev => (
            prev.includes(columnKey)
                ? prev.filter(key => key !== columnKey)
                : [...prev, columnKey]
        ));
    };

    const handleModeChange = (modeKey) => {
        if (!supportsColumnModes) return;
        const preset = columnModePresets[modeKey];
        if (!Array.isArray(preset)) return;
        setVisibleOptionalColumns(preset);
    };

    const handleResetColumns = () => {
        if (supportsColumnModes) {
            const modeForReset = selectedColumnMode !== 'custom' ? selectedColumnMode : defaultColumnMode;
            const preset = columnModePresets[modeForReset] || columnModePresets[defaultColumnMode];
            setVisibleOptionalColumns(preset);
            return;
        }
        setVisibleOptionalColumns(defaultVisibleOptionalColumns);
    };

    if (isLoading) {
        const skeletonColumns = 1 + visibleOptionalColumns.length;
        return <TableSkeleton rows={8} columns={Math.max(3, skeletonColumns)} showAdmin={showSerialNumber} />;
    }
    const hasTalents = Array.isArray(talents) && talents.length > 0;

    return (
        <div className="talent-table-container bg-[var(--surface)] shadow-sm rounded-xl overflow-visible border border-[var(--border)]">
            <div className="px-4 py-3 border-b border-[var(--border)] flex justify-end" ref={columnMenuRef}>
                <div className="relative">
                    <button
                        type="button"
                        onClick={() => setIsColumnMenuOpen(prev => !prev)}
                        className="px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] bg-[var(--neutral-100)] hover:bg-[var(--neutral-200)] rounded-md border border-[var(--border)] transition-colors"
                    >
                        表示列
                    </button>

                    {isColumnMenuOpen && (
                        <div className="absolute right-0 mt-2 w-72 bg-[var(--surface)] border border-[var(--border)] rounded-lg shadow-lg z-40 p-3">
                            {supportsColumnModes && (
                                <div className="mb-3 pb-3 border-b border-[var(--border)]">
                                    <div className="text-xs font-semibold text-[var(--text-secondary)] mb-2">表示モード</div>
                                    <div
                                        className="grid gap-2"
                                        style={{ gridTemplateColumns: `repeat(${columnModeOptions.length}, minmax(0, 1fr))` }}
                                    >
                                        {columnModeOptions.map(mode => (
                                            <button
                                                key={mode.key}
                                                type="button"
                                                onClick={() => handleModeChange(mode.key)}
                                                className={`px-2 py-1.5 text-xs rounded-md border transition-colors ${selectedColumnMode === mode.key
                                                        ? 'bg-[var(--primary-50)] text-[var(--primary-700)] border-[var(--primary-200)]'
                                                        : 'bg-[var(--neutral-50)] text-[var(--text-secondary)] border-[var(--border)] hover:bg-[var(--neutral-100)]'
                                                    }`}
                                            >
                                                {mode.label}
                                            </button>
                                        ))}
                                    </div>
                                    <div className="mt-2 text-[11px] text-[var(--text-muted)]">
                                        現在: {columnModeLabels[selectedColumnMode] || columnModeLabels.custom}
                                    </div>
                                </div>
                            )}
                            <div className="text-xs font-semibold text-[var(--text-secondary)] mb-2">表示/非表示</div>
                            <div className="space-y-2 max-h-64 overflow-y-auto">
                                {menuColumns.map(column => (
                                    <label key={column.key} className="flex items-center text-xs text-[var(--text-primary)] cursor-pointer">
                                        <input
                                            type="checkbox"
                                            checked={isColumnVisible(column.key)}
                                            onChange={() => handleColumnToggle(column.key)}
                                            className="mr-2 w-4 h-4 text-[var(--primary-600)] border-[var(--border)] rounded focus:ring-[var(--primary-300)]"
                                        />
                                        {column.label}
                                    </label>
                                ))}
                            </div>
                            <button
                                type="button"
                                onClick={handleResetColumns}
                                className="mt-3 w-full px-3 py-1.5 text-xs font-medium text-[var(--text-secondary)] bg-[var(--neutral-100)] hover:bg-[var(--neutral-200)] rounded-md transition-colors"
                            >
                                初期表示に戻す
                            </button>
                        </div>
                    )}
                </div>
            </div>
            <div className="sticky top-0 z-20 bg-[var(--surface)] border-b border-[var(--border)]">
                <div
                    className="table-scrollbar table-scrollbar--top"
                    ref={topScrollRef}
                    onScroll={handleTopScroll}
                    aria-hidden="true"
                >
                    <div style={{ width: `${scrollWidth}px` }} />
                </div>
            </div>
            <div className="table-scrollbar--hidden overflow-x-auto overflow-y-auto" style={{ maxHeight: 'calc(100vh - 250px)' }} ref={mainScrollRef} onScroll={handleMainScroll}>
                <table className={`talent-table min-w-full divide-y divide-[var(--border)] ${!showSerialNumber ? 'no-serial' : ''}`} style={{ tableLayout: 'fixed' }}>
                    <thead className="bg-[var(--neutral-50)] sticky top-0 z-10">
                        <tr>
                            {showSerialNumber && (
                                <ResizableHeader
                                    columnKey="serialNumber"
                                    width={columnWidths.serialNumber}
                                    onResize={handleColumnResize}
                                    minWidth={50}
                                    className="text-center"
                                    frozenClass="frozen-left-1"
                                    frozenStyle={{ left: 0 }}
                                >
                                    {t('serialNumber')}
                                </ResizableHeader>
                            )}
                            <ResizableSortableHeader
                                columnKey="fullName"
                                width={columnWidths.fullName}
                                onResize={handleColumnResize}
                                frozenClass="frozen-left-2"
                                frozenStyle={{ left: showSerialNumber ? columnWidths.serialNumber : 0 }}
                                {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                            >
                                {t('name')}
                            </ResizableSortableHeader>
                            {isColumnVisible('managementNumber') && (
                                <ResizableSortableHeader
                                    columnKey="managementNumber"
                                    width={columnWidths.managementNumber}
                                    onResize={handleColumnResize}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('managementNumber')}
                                </ResizableSortableHeader>
                            )}
                            {isColumnVisible('affiliatedOrganizationName') && (
                                <ResizableSortableHeader
                                    columnKey="affiliatedOrganizationName"
                                    width={columnWidths.affiliatedOrganizationName}
                                    onResize={handleColumnResize}
                                    minWidth={120}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('affiliatedOrganization')}
                                </ResizableSortableHeader>
                            )}
                            {isColumnVisible('workplaceName') && (
                                <ResizableSortableHeader
                                    columnKey="workplaceName"
                                    width={columnWidths.workplaceName}
                                    onResize={handleColumnResize}
                                    minWidth={120}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('workplace')}
                                </ResizableSortableHeader>
                            )}
                            {isColumnVisible('initialCost') && (
                                <ResizableSortableHeader
                                    columnKey="initialCost"
                                    width={columnWidths.initialCost}
                                    onResize={handleColumnResize}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {renderFeeHeader(t('initialCost'), feeTotals?.initialCost)}
                                </ResizableSortableHeader>
                            )}
                            {isColumnVisible('managementFee') && (
                                <ResizableSortableHeader
                                    columnKey="managementFee"
                                    width={columnWidths.managementFee}
                                    onResize={handleColumnResize}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {renderFeeHeader(t('managementFee'), feeTotals?.managementFee)}
                                </ResizableSortableHeader>
                            )}
                            {isColumnVisible('nationality') && (
                                <ResizableSortableHeader
                                    columnKey="nationality"
                                    width={columnWidths.nationality}
                                    onResize={handleColumnResize}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('nationality')}
                                </ResizableSortableHeader>
                            )}
                            {isColumnVisible('field') && (
                                <ResizableSortableHeader
                                    columnKey="field"
                                    width={columnWidths.field}
                                    onResize={handleColumnResize}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('field')}
                                </ResizableSortableHeader>
                            )}
                            {showResponsibleSales && isColumnVisible('salesStaff') && (
                                <ResizableSortableHeader
                                    columnKey="salesStaff"
                                    width={columnWidths.salesStaff}
                                    onResize={handleColumnResize}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('responsibleSales')}
                                </ResizableSortableHeader>
                            )}
                            {isColumnVisible('status') && (
                                <ResizableSortableHeader
                                    columnKey="status"
                                    width={columnWidths.status}
                                    onResize={handleColumnResize}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('employmentStatus')}
                                </ResizableSortableHeader>
                            )}
                            {canViewResidenceColumns && isColumnVisible('residenceCardNumber') && (
                                <ResizableSortableHeader
                                    columnKey="residenceCardNumber"
                                    width={columnWidths.residenceCardNumber}
                                    onResize={handleColumnResize}
                                    minWidth={130}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('residenceCardNumber')}
                                </ResizableSortableHeader>
                            )}
                            {canViewResidenceColumns && isColumnVisible('residenceCardExpiry') && (
                                <ResizableSortableHeader
                                    columnKey="residenceCardExpiry"
                                    width={columnWidths.residenceCardExpiry}
                                    onResize={handleColumnResize}
                                    minWidth={120}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('residenceCardExpiry')}
                                </ResizableSortableHeader>
                            )}
                            {canViewResidenceColumns && isColumnVisible('residenceInfoUpdatedAt') && (
                                <ResizableSortableHeader
                                    columnKey="residenceInfoUpdatedAt"
                                    width={columnWidths.residenceInfoUpdatedAt}
                                    onResize={handleColumnResize}
                                    minWidth={140}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    在留情報更新日
                                </ResizableSortableHeader>
                            )}
                            {canViewResidenceColumns && isColumnVisible('startDate') && (
                                <ResizableSortableHeader
                                    columnKey="startDate"
                                    width={columnWidths.startDate}
                                    onResize={handleColumnResize}
                                    minWidth={110}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('startDate')}
                                </ResizableSortableHeader>
                            )}
                            {canViewResidenceColumns && isColumnVisible('endDate') && (
                                <ResizableSortableHeader
                                    columnKey="endDate"
                                    width={columnWidths.endDate}
                                    onResize={handleColumnResize}
                                    minWidth={110}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('endDate')}
                                </ResizableSortableHeader>
                            )}
                            {canViewResidenceColumns && isColumnVisible('employmentDays') && (
                                <ResizableSortableHeader
                                    columnKey="employmentDays"
                                    width={columnWidths.employmentDays}
                                    onResize={handleColumnResize}
                                    minWidth={110}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    {t('employmentDays')}
                                </ResizableSortableHeader>
                            )}
                            {isAdmin && isColumnVisible('activeLoanBorrowedAmount') && (
                                <ResizableSortableHeader
                                    columnKey="activeLoanBorrowedAmount"
                                    width={columnWidths.activeLoanBorrowedAmount}
                                    onResize={handleColumnResize}
                                    minWidth={110}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    借入
                                </ResizableSortableHeader>
                            )}
                            {isAdmin && isColumnVisible('activeLoanRepaidAmount') && (
                                <ResizableSortableHeader
                                    columnKey="activeLoanRepaidAmount"
                                    width={columnWidths.activeLoanRepaidAmount}
                                    onResize={handleColumnResize}
                                    minWidth={110}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    返済
                                </ResizableSortableHeader>
                            )}
                            {isAdmin && isColumnVisible('activeLoanRemainingAmount') && (
                                <ResizableSortableHeader
                                    columnKey="activeLoanRemainingAmount"
                                    width={columnWidths.activeLoanRemainingAmount}
                                    onResize={handleColumnResize}
                                    minWidth={110}
                                    {...{ sortBy, sortDirection, setSortBy, setSortDirection }}
                                >
                                    残額
                                </ResizableSortableHeader>
                            )}
                            <ResizableHeader
                                columnKey="actions"
                                width={columnWidths.actions}
                                onResize={handleColumnResize}
                                minWidth={150}
                                className="text-right"
                                frozenClass="frozen-right"
                                frozenStyle={{ right: 0 }}
                            >
                                {t('actions')}
                            </ResizableHeader>
                        </tr>
                    </thead>
                    <tbody className="bg-[var(--surface)] divide-y divide-[var(--border)]">
                        {hasTalents ? talents.map((talent, index) => (
                            <tr key={talent.id} className="hover:bg-[var(--neutral-50)] transition-colors duration-150">
                                {showSerialNumber && (
                                    <td
                                        className="frozen-left-1 px-4 py-4 whitespace-nowrap text-sm text-center text-[var(--text-secondary)] font-medium"
                                        style={{ width: `${columnWidths.serialNumber}px`, left: 0 }}
                                    >
                                        <span className="inline-flex items-center justify-center w-8 h-8 bg-[var(--neutral-100)] text-[var(--text-secondary)] rounded-full text-xs font-semibold">
                                            {index + 1}
                                        </span>
                                    </td>
                                )}
                                <td
                                    className="frozen-left-2 px-6 py-4 whitespace-nowrap text-sm font-medium text-[var(--text-primary)] truncate"
                                    style={{ width: `${columnWidths.fullName}px`, maxWidth: `${columnWidths.fullName}px`, left: showSerialNumber ? columnWidths.serialNumber : 0 }}
                                >
                                    {talent.fullName}
                                </td>
                                {isColumnVisible('managementNumber') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.managementNumber}px`, maxWidth: `${columnWidths.managementNumber}px` }}
                                    >
                                        {talent.managementNumber}
                                    </td>
                                )}
                                {isColumnVisible('affiliatedOrganizationName') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)] truncate"
                                        style={{ width: `${columnWidths.affiliatedOrganizationName}px`, maxWidth: `${columnWidths.affiliatedOrganizationName}px` }}
                                    >
                                        {talent.affiliatedOrganizationName}
                                    </td>
                                )}
                                {isColumnVisible('workplaceName') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)] truncate"
                                        style={{ width: `${columnWidths.workplaceName}px`, maxWidth: `${columnWidths.workplaceName}px` }}
                                    >
                                        {talent.workplaceName || <span className="status-badge status-inactive">{t('unregistered')}</span>}
                                    </td>
                                )}
                                {isColumnVisible('initialCost') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.initialCost}px` }}
                                    >
                                        {getCostDisplay(talent.initialCost, talent.initialCostNotCharged)}
                                    </td>
                                )}
                                {isColumnVisible('managementFee') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.managementFee}px` }}
                                    >
                                        {getCostDisplay(talent.managementFee, talent.managementFeeNotCharged, true)}
                                    </td>
                                )}
                                {isColumnVisible('nationality') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.nationality}px` }}
                                    >
                                        {getJapaneseNationality(talent.nationality)}
                                    </td>
                                )}
                                {isColumnVisible('field') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.field}px` }}
                                    >
                                        <span className={`status-badge ${talent.field ? 'status-active' : 'status-inactive'}`}>
                                            {talent.field ? getJapaneseField(talent.field) : t('unregistered')}
                                        </span>
                                    </td>
                                )}
                                {showResponsibleSales && isColumnVisible('salesStaff') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.salesStaff}px` }}
                                    >
                                        <span className={`status-badge ${talent.salesStaff?.username ? 'bg-[var(--primary-100)] text-[var(--primary-800)]' : 'status-inactive'}`}>
                                            {talent.salesStaff?.username || t('notAssigned')}
                                        </span>
                                    </td>
                                )}
                                {isColumnVisible('status') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.status}px` }}
                                    >
                                        <span className={`status-badge ${getEmploymentStatusClass(talent.employmentStatus, talent.employmentPauseType)}`}>
                                            {getEmploymentStatusLabel(talent)}
                                        </span>
                                    </td>
                                )}
                                {canViewResidenceColumns && isColumnVisible('residenceCardNumber') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.residenceCardNumber}px` }}
                                    >
                                        {(talent.residence || talent.residencePermit)?.residenceCardNumber || (
                                            <span className="status-badge status-inactive">{t('unregistered')}</span>
                                        )}
                                    </td>
                                )}
                                {canViewResidenceColumns && isColumnVisible('residenceCardExpiry') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.residenceCardExpiry}px` }}
                                    >
                                        {formatDateValue((talent.residence || talent.residencePermit)?.residenceCardExpiry)}
                                    </td>
                                )}
                                {canViewResidenceColumns && isColumnVisible('residenceInfoUpdatedAt') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.residenceInfoUpdatedAt}px` }}
                                    >
                                        {formatDateValue((talent.residence || talent.residencePermit)?.residenceInfoUpdatedAt)}
                                    </td>
                                )}
                                {canViewResidenceColumns && isColumnVisible('startDate') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.startDate}px` }}
                                    >
                                        {formatDateValue((talent.residence || talent.residencePermit)?.startDate)}
                                    </td>
                                )}
                                {canViewResidenceColumns && isColumnVisible('endDate') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.endDate}px` }}
                                    >
                                        {formatDateValue((talent.residence || talent.residencePermit)?.endDate)}
                                    </td>
                                )}
                                {canViewResidenceColumns && isColumnVisible('employmentDays') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.employmentDays}px` }}
                                    >
                                        {formatEmploymentDays(talent)}
                                    </td>
                                )}
                                {isAdmin && isColumnVisible('activeLoanBorrowedAmount') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.activeLoanBorrowedAmount}px` }}
                                    >
                                        {formatYen(getLoanMetricValue(talent, 'activeLoanBorrowedAmount'))}
                                    </td>
                                )}
                                {isAdmin && isColumnVisible('activeLoanRepaidAmount') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.activeLoanRepaidAmount}px` }}
                                    >
                                        {formatYen(getLoanMetricValue(talent, 'activeLoanRepaidAmount'))}
                                    </td>
                                )}
                                {isAdmin && isColumnVisible('activeLoanRemainingAmount') && (
                                    <td
                                        className="px-6 py-4 whitespace-nowrap text-sm text-[var(--text-secondary)]"
                                        style={{ width: `${columnWidths.activeLoanRemainingAmount}px` }}
                                    >
                                        {formatYen(getLoanMetricValue(talent, 'activeLoanRemainingAmount'))}
                                    </td>
                                )}
                                <td
                                    className="frozen-right px-6 py-4 whitespace-nowrap text-right text-sm font-medium"
                                    style={{ width: `${columnWidths.actions}px`, right: 0 }}
                                >
                                    <div className="action-buttons flex flex-wrap justify-end space-x-2">
                                        {isSales &&
                                            <button
                                                onClick={() => onViewEdit(talent)}
                                                className="px-3 py-1.5 text-xs font-medium text-[var(--success-700)] bg-[var(--success-50)] hover:bg-[var(--success-100)] border border-[var(--success-200)] rounded-md transition-all duration-200 hover:shadow-sm"
                                            >
                                                {t('viewEdit')}
                                            </button>
                                        }
                                        {isResidence &&
                                            <button
                                                onClick={() => onViewEdit?.(talent.id)}
                                                className="px-3 py-1.5 text-xs font-medium text-[var(--primary-700)] bg-[var(--primary-50)] hover:bg-[var(--primary-100)] border border-[var(--primary-200)] rounded-md transition-all duration-200 hover:shadow-sm"
                                            >
                                                {t('viewEdit')}
                                            </button>
                                        }
                                        {isAdmin &&
                                            <button
                                                onClick={() => onViewEdit(talent.id)}
                                                className="px-3 py-1.5 text-xs font-medium text-[var(--primary-700)] bg-[var(--primary-50)] hover:bg-[var(--primary-100)] border border-[var(--primary-200)] rounded-md transition-all duration-200 hover:shadow-sm"
                                            >
                                                {t('viewEdit')}
                                            </button>
                                        }
                                        {isAdmin &&
                                            <button
                                                onClick={() => onTransferTalent(talent)}
                                                className="px-3 py-1.5 text-xs font-medium text-[var(--info-700)] bg-[var(--info-50)] hover:bg-[var(--info-100)] border border-[var(--info-200)] rounded-md transition-all duration-200 hover:shadow-sm"
                                            >
                                                {t('transferTalent')}
                                            </button>
                                        }
                                        {isAdmin &&
                                            <button
                                                onClick={() => handleDeleteClick(talent)}
                                                disabled={isDeleting === talent.id}
                                                className="px-3 py-1.5 text-xs font-medium text-[var(--error-700)] bg-[var(--error-50)] hover:bg-[var(--error-100)] border border-[var(--error-200)] rounded-md transition-all duration-200 hover:shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                                            >
                                                {isDeleting === talent.id ? (
                                                    <div className="flex items-center space-x-1">
                                                        <div className="w-3 h-3 border border-[var(--error-300)] border-t-transparent rounded-full animate-spin"></div>
                                                        <span>{t('deleting')}</span>
                                                    </div>
                                                ) : (
                                                    t('delete')
                                                )}
                                            </button>
                                        }
                                    </div>
                                </td>
                            </tr>
                        )) : (
                            <tr>
                                <td colSpan={100} className="p-12 text-center text-[var(--text-muted)]">
                                    {t('noTalentDataFound')}
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ConfirmDialog
                isOpen={deleteConfirm.isOpen}
                onClose={handleDeleteCancel}
                onConfirm={handleDeleteConfirm}
                title={t('deleteConfirmationTitle')}
                message={t('deleteConfirmationMessage', { name: deleteConfirm.talent?.fullName })}
                confirmText={t('delete')}
                cancelText={t('cancel')}
                type="error"
            />
        </div>
    );
}
